import { useCallback, useEffect, useRef, useState } from "react";

import { streamSse } from "@utils/api/sse";

export type TurnRunnerPhase =
  | "idle"
  | "restoring"
  | "starting"
  | "streaming"
  | "reconnecting"
  | "awaiting";

type PersistedTurnSession = {
  conversationId: string;
  turnIndex: number;
  pendingJobId?: string | null;
};

export type StreamCallbackHelpers = {
  markTerminal: () => void;
  finishTurn: () => void;
  setRetryable: (value: boolean) => void;
};

type CreateTurnErrorResult = {
  retryable?: boolean;
};

export type UseSseTurnRunnerOptions<TInput, TJob, TRestore> = {
  storageKey: string;
  storagePrefix?: string;
  stopGraceMs?: number;
  createTurn: (args: {
    input: TInput;
    conversationId: string | null;
    turnIndex: number;
  }) => Promise<TJob>;
  getConversationId: (job: TJob) => string;
  getJobId: (job: TJob) => string;
  jobStreamPath: (jobId: string) => string;
  stopTurn: (jobId: string) => Promise<unknown>;
  restoreConversation: (conversationId: string) => Promise<TRestore>;
  isAwaiting: () => boolean;
  onReplay?: () => void;
  onBeforeRun?: () => void;
  onStreamPayload: (
    payload: Record<string, unknown>,
    helpers: StreamCallbackHelpers,
  ) => void;
  onCreateTurnError?: (error: unknown) => CreateTurnErrorResult | void;
  onStreamError?: (error: unknown) => void;
  onStopError?: (error: unknown) => void;
  onRestore?: (
    restored: TRestore,
    context: { pendingJobId: string | null },
  ) => void;
  onStartNew?: () => void;
};

export type UseSseTurnRunnerResult<TInput> = {
  phase: TurnRunnerPhase;
  busy: boolean;
  retryable: boolean;
  stopping: boolean;
  hasConversation: boolean;
  runTurn: (input: TInput, options?: { replay?: boolean }) => void;
  retry: () => void;
  stop: () => Promise<void>;
  startNew: () => void;
};

const DEFAULT_STORAGE_PREFIX = "sse-turn-runner";
const DEFAULT_STOP_GRACE_MS = 10_000;

const storageKeyFor = (prefix: string, key: string) => `${prefix}:${key}`;

const readPersisted = (
  prefix: string,
  key: string,
): PersistedTurnSession | null => {
  try {
    const raw = window.sessionStorage.getItem(storageKeyFor(prefix, key));
    return raw ? (JSON.parse(raw) as PersistedTurnSession) : null;
  } catch {
    return null;
  }
};

const writePersisted = (
  prefix: string,
  key: string,
  value: PersistedTurnSession | null,
) => {
  try {
    if (value) {
      window.sessionStorage.setItem(
        storageKeyFor(prefix, key),
        JSON.stringify(value),
      );
    } else {
      window.sessionStorage.removeItem(storageKeyFor(prefix, key));
    }
  } catch {
    // If storage fails, chat still works without reload recovery.
  }
};

export const useSseTurnRunner = <TInput, TJob, TRestore>({
  storageKey,
  storagePrefix = DEFAULT_STORAGE_PREFIX,
  stopGraceMs = DEFAULT_STOP_GRACE_MS,
  createTurn,
  getConversationId,
  getJobId,
  jobStreamPath,
  stopTurn,
  restoreConversation,
  isAwaiting,
  onReplay,
  onBeforeRun,
  onStreamPayload,
  onCreateTurnError,
  onStreamError,
  onStopError,
  onRestore,
  onStartNew,
}: UseSseTurnRunnerOptions<
  TInput,
  TJob,
  TRestore
>): UseSseTurnRunnerResult<TInput> => {
  const [phase, setPhase] = useState<TurnRunnerPhase>("idle");
  const [retryable, setRetryable] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [hasConversation, setHasConversation] = useState(false);

  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);
  const stopTimerRef = useRef<number | null>(null);
  const completeRef = useRef(false);
  const conversationIdRef = useRef<string | null>(null);
  const jobIdRef = useRef<string | null>(null);
  const turnIndexRef = useRef(0);
  const lastInputRef = useRef<TInput | null>(null);
  const restoreConversationRef = useRef(restoreConversation);
  const onRestoreRef = useRef(onRestore);
  const onStartNewRef = useRef(onStartNew);

  useEffect(() => {
    restoreConversationRef.current = restoreConversation;
    onRestoreRef.current = onRestore;
    onStartNewRef.current = onStartNew;
  }, [onRestore, onStartNew, restoreConversation]);

  const persist = useCallback(() => {
    if (!conversationIdRef.current) {
      writePersisted(storagePrefix, storageKey, null);
      return;
    }
    writePersisted(storagePrefix, storageKey, {
      conversationId: conversationIdRef.current,
      turnIndex: turnIndexRef.current,
      pendingJobId: jobIdRef.current,
    });
  }, [storageKey, storagePrefix]);

  const markTerminal = useCallback(() => {
    completeRef.current = true;
  }, []);

  const finishTurn = useCallback(() => {
    completeRef.current = true;
    turnIndexRef.current += 1;
    jobIdRef.current = null;
    setRetryable(false);
    persist();
  }, [persist]);

  const startNew = useCallback(() => {
    if (stopTimerRef.current !== null) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    abortRef.current?.abort();
    completeRef.current = true;
    conversationIdRef.current = null;
    jobIdRef.current = null;
    turnIndexRef.current = 0;
    lastInputRef.current = null;
    writePersisted(storagePrefix, storageKey, null);
    setRetryable(false);
    setHasConversation(false);
    setPhase("idle");
    onStartNewRef.current?.();
  }, [storageKey, storagePrefix]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (stopTimerRef.current !== null) {
        window.clearTimeout(stopTimerRef.current);
      }
      abortRef.current?.abort();
    };
  }, []);

  const attach = useCallback(
    async (jobId: string, controller: AbortController) => {
      setPhase("streaming");
      try {
        await streamSse({
          path: jobStreamPath(jobId),
          signal: controller.signal,
          isComplete: () => completeRef.current,
          onRetry: () => {
            if (mountedRef.current) setPhase("reconnecting");
          },
          onFrame: (frame) => {
            if (!mountedRef.current) return;
            setPhase("streaming");

            if (frame.event === "done") {
              finishTurn();
              return;
            }

            let payload: Record<string, unknown>;
            try {
              payload = JSON.parse(frame.data) as Record<string, unknown>;
            } catch {
              return;
            }

            onStreamPayload(payload, {
              markTerminal,
              finishTurn,
              setRetryable,
            });
          },
        });
      } catch (error) {
        if (!mountedRef.current || controller.signal.aborted) return;
        markTerminal();
        onStreamError?.(error);
        setRetryable(true);
      }

      if (!mountedRef.current || controller.signal.aborted) return;
      setPhase(isAwaiting() ? "awaiting" : "idle");
      persist();
    },
    [
      finishTurn,
      isAwaiting,
      jobStreamPath,
      markTerminal,
      onStreamError,
      onStreamPayload,
      persist,
    ],
  );

  const runTurnImpl = useCallback(
    async (input: TInput, replay: boolean) => {
      if (stopTimerRef.current !== null) {
        window.clearTimeout(stopTimerRef.current);
        stopTimerRef.current = null;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      completeRef.current = false;
      lastInputRef.current = input;
      onBeforeRun?.();
      setRetryable(false);
      setPhase("starting");

      let job: TJob;
      try {
        job = await createTurn({
          input,
          conversationId: conversationIdRef.current,
          turnIndex: turnIndexRef.current,
        });
      } catch (error) {
        if (!mountedRef.current) return;
        const result = onCreateTurnError?.(error);
        setRetryable(result?.retryable ?? true);
        setPhase("idle");
        return;
      }

      if (!mountedRef.current || controller.signal.aborted) return;

      conversationIdRef.current = getConversationId(job);
      jobIdRef.current = getJobId(job);
      setHasConversation(true);
      persist();

      if (replay) onReplay?.();

      await attach(jobIdRef.current, controller);
    },
    [
      attach,
      createTurn,
      getConversationId,
      getJobId,
      onBeforeRun,
      onCreateTurnError,
      onReplay,
      persist,
    ],
  );

  const runTurn = useCallback(
    (input: TInput, options?: { replay?: boolean }) => {
      void runTurnImpl(input, options?.replay ?? false);
    },
    [runTurnImpl],
  );

  const retry = useCallback(() => {
    const input = lastInputRef.current;
    if (!input) return;
    void runTurnImpl(input, true);
  }, [runTurnImpl]);

  const stop = useCallback(async () => {
    const jobId = jobIdRef.current;
    const controller = abortRef.current;
    if (!jobId) return;
    setStopping(true);
    try {
      await stopTurn(jobId);
      if (stopTimerRef.current !== null) {
        window.clearTimeout(stopTimerRef.current);
      }
      stopTimerRef.current = window.setTimeout(() => {
        stopTimerRef.current = null;
        if (
          !mountedRef.current ||
          completeRef.current ||
          jobIdRef.current !== jobId ||
          abortRef.current !== controller
        ) {
          return;
        }
        controller?.abort();
        markTerminal();
        setPhase("idle");
        setRetryable(true);
      }, stopGraceMs);
    } catch (error) {
      if (mountedRef.current) onStopError?.(error);
    } finally {
      if (mountedRef.current) setStopping(false);
    }
  }, [markTerminal, onStopError, stopGraceMs, stopTurn]);

  useEffect(() => {
    const persisted = readPersisted(storagePrefix, storageKey);
    if (!persisted?.conversationId) return undefined;

    conversationIdRef.current = persisted.conversationId;
    turnIndexRef.current = persisted.turnIndex;
    setHasConversation(true);
    setPhase("restoring");

    let cancelled = false;
    restoreConversationRef
      .current(persisted.conversationId)
      .then((restored) => {
        if (cancelled || !mountedRef.current) return;
        onRestoreRef.current?.(restored, {
          pendingJobId: persisted.pendingJobId ?? null,
        });
      })
      .catch(() => {
        if (cancelled || !mountedRef.current) return;
        startNew();
      })
      .finally(() => {
        if (!cancelled && mountedRef.current) setPhase("idle");
      });

    return () => {
      cancelled = true;
    };
    // Restore on mount/key change only
  }, [storageKey, storagePrefix]);

  return {
    phase,
    busy:
      phase === "starting" || phase === "streaming" || phase === "reconnecting",
    retryable,
    stopping,
    hasConversation,
    runTurn,
    retry,
    stop,
    startNew,
  };
};
